import {
    Dialog,
    DialogContent,
    DialogTitle,
    IconButton,
    Box,
    Typography,
    Button,
    Chip,
    Divider,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import ShoppingCartIcon from "@mui/icons-material/ShoppingCart";
import MenuBookIcon from "@mui/icons-material/MenuBook";
import ArticleIcon from "@mui/icons-material/Article";
import EventIcon from "@mui/icons-material/Event";
import DevicesIcon from "@mui/icons-material/Devices";
import PodcastsIcon from "@mui/icons-material/Podcasts";
import MedicalServicesIcon from "@mui/icons-material/MedicalServices";
import StorefrontIcon from "@mui/icons-material/Storefront";
import themeColors from "../theme";

const categoryConfig = {
    Books: { icon: MenuBookIcon, label: "Book Details" },
    "Medical Journals": { icon: ArticleIcon, label: "Journal Details" },
    Conferences: { icon: EventIcon, label: "Conference Details" },
    "Digital Apps": { icon: DevicesIcon, label: "App Details" },
    "Online Platforms": { icon: DevicesIcon, label: "Platform Details" },
    Podcast: { icon: PodcastsIcon, label: "Podcast Details" },
    "Medical Equipment": { icon: MedicalServicesIcon, label: "Equipment Details" },
};

const defaultConfig = { icon: StorefrontIcon, label: "Product Details" };

export default function BookDetailModal({ book: product, open, onClose }) {
    const { teal, darkTeal } = themeColors;

    if (!product) return null;

    const isFree = !product.price || product.price === 0;
    const config = categoryConfig[product.category] || defaultConfig;
    const IconComp = config.icon;
    // Users are sent through the affiliate link; purchaseLink stays the canonical
    // Amazon URL because price scraping and ASIN matching depend on it.
    const purchaseUrl = product.affiliateLink || product.purchaseLink;
    const hasPurchaseLink = !!purchaseUrl;
    const categories = product.categories || [];
    const fallbackImage = product.category === "Medical Equipment"
        ? "/images/medical-equipment/default.svg"
        : "/images/books/book-default.png";

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="md"
            fullWidth
            PaperProps={{
                sx: {
                    borderRadius: 3,
                    overflow: "hidden",
                },
            }}
        >
            <DialogTitle
                sx={{
                    background: `linear-gradient(135deg, ${teal}, ${darkTeal})`,
                    color: "white",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    py: 2,
                }}
            >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <IconComp />
                    <Typography variant="h6" fontWeight={600}>
                        {config.label}
                    </Typography>
                </Box>
                <IconButton onClick={onClose} sx={{ color: "white" }}>
                    <CloseIcon />
                </IconButton>
            </DialogTitle>

            <DialogContent sx={{ p: 0 }}>
                <Box
                    sx={{
                        display: "flex",
                        flexDirection: { xs: "column", md: "row" },
                        gap: 0,
                    }}
                >
                    {/* Product Image */}
                    <Box
                        sx={{
                            width: { xs: "100%", md: 300 },
                            minHeight: 300,
                            backgroundColor: "#e8f0f2",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            p: 3,
                            flexShrink: 0,
                        }}
                    >
                        <Box
                            component="img"
                            src={product.image || fallbackImage}
                            alt={product.title || product.name}
                            onError={(e) => {
                                e.target.onerror = null;
                                e.target.src = fallbackImage;
                            }}
                            sx={{
                                maxWidth: "100%",
                                maxHeight: 260,
                                objectFit: "contain",
                                borderRadius: 2,
                                boxShadow: "0 8px 30px rgba(0,0,0,0.15)",
                            }}
                        />
                    </Box>

                    {/* Product Info */}
                    <Box sx={{ flex: 1, p: 4 }}>
                        <Typography
                            variant="h5"
                            fontWeight={700}
                            color="#2C3E50"
                            gutterBottom
                        >
                            {product.title || product.name}
                        </Typography>

                        {product.edition && (
                            <Typography
                                variant="body1"
                                color="text.secondary"
                                sx={{ mb: 2 }}
                            >
                                {product.edition} Edition
                            </Typography>
                        )}

                        {/* Categories */}
                        {categories.length > 0 && (
                            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.8, mb: 3 }}>
                                {categories.map((cat) => (
                                    <Chip
                                        key={cat}
                                        label={cat}
                                        size="small"
                                        sx={{
                                            backgroundColor: `${teal}15`,
                                            color: teal,
                                            fontWeight: 500,
                                            border: `1px solid ${teal}30`,
                                        }}
                                    />
                                ))}
                            </Box>
                        )}

                        <Divider sx={{ mb: 3 }} />

                        {/* Details Grid */}
                        <Box sx={{ display: "grid", gridTemplateColumns: "120px 1fr", gap: 1.5, mb: 3 }}>
                            {product.isbn && (
                                <>
                                    <Typography variant="body2" color="text.secondary" fontWeight={600}>
                                        ISBN-13:
                                    </Typography>
                                    <Typography variant="body2">{product.isbn}</Typography>
                                </>
                            )}
                            {product.brand && (
                                <>
                                    <Typography variant="body2" color="text.secondary" fontWeight={600}>
                                        Brand:
                                    </Typography>
                                    <Typography variant="body2">{product.brand}</Typography>
                                </>
                            )}
                            {product.model && (
                                <>
                                    <Typography variant="body2" color="text.secondary" fontWeight={600}>
                                        Model:
                                    </Typography>
                                    <Typography variant="body2">{product.model}</Typography>
                                </>
                            )}
                            <Typography variant="body2" color="text.secondary" fontWeight={600}>
                                Category:
                            </Typography>
                            <Typography variant="body2">{product.category || "General"}</Typography>
                        </Box>

                        {/* Price */}
                        <Box
                            sx={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "space-between",
                                mt: 2,
                                p: 2.5,
                                backgroundColor: "#f8fafa",
                                borderRadius: 2,
                                border: "1px solid #e0e8ea",
                            }}
                        >
                            <Box>
                                <Typography variant="body2" color="text.secondary">
                                    Price
                                </Typography>
                                <Typography
                                    variant="h4"
                                    fontWeight={700}
                                    sx={{ color: teal }}
                                >
                                    {isFree
                                        ? "Free"
                                        : `$${Number(product.price).toFixed(2)}`}
                                </Typography>
                                {product.lastFetchedAt && (
                                    <Typography variant="caption" color="text.secondary">
                                        Updated {new Date(product.lastFetchedAt).toLocaleDateString()}
                                    </Typography>
                                )}
                            </Box>

                            {hasPurchaseLink && (
                                <Button
                                    variant="contained"
                                    size="large"
                                    startIcon={<ShoppingCartIcon />}
                                    onClick={() => window.open(purchaseUrl, "_blank", "noopener,noreferrer")}
                                    sx={{
                                        background: `linear-gradient(135deg, ${teal}, ${darkTeal})`,
                                        color: "white",
                                        textTransform: "none",
                                        fontWeight: 600,
                                        fontSize: "1rem",
                                        px: 4,
                                        py: 1.5,
                                        borderRadius: 2,
                                        boxShadow: `0 4px 15px ${teal}40`,
                                        "&:hover": {
                                            background: `linear-gradient(135deg, ${darkTeal}, ${teal})`,
                                            boxShadow: `0 6px 20px ${teal}60`,
                                        },
                                    }}
                                >
                                    {isFree ? "Get Now" : "Purchase"}
                                </Button>
                            )}
                        </Box>
                    </Box>
                </Box>
            </DialogContent>
        </Dialog>
    );
}
